import { filter, first } from 'lodash';

import { CourseModes } from 'bundles/enroll-common/components/StartDateString';
import type { FormattedCourse } from 'bundles/enroll-common/types/enrollSectionTypes';
import type { CourseQueryEnterpriseLegacyLearnerHomeQuery } from 'bundles/enterprise-legacy-learner-home/queries/__generated__/CourseQueryEnterpriseLegacyLearnerHome';

import _t from 'i18n!nls/enterprise-legacy-learner-home';

export type { CourseTypeMetadata } from 'bundles/enterprise-legacy-learner-home/utils/courseTypeUtils';
export {
  isGuidedProject,
  isProject,
  isProjectOrSelfPacedProject,
  getCourseType,
} from 'bundles/enterprise-legacy-learner-home/utils/courseTypeUtils';

const getButtonTextByCourseProgress = (summaryCoreProgressPercentage = 0) => {
  return summaryCoreProgressPercentage === 100 ? _t('Review') : _t('Resume');
};

const getTrackingButtonTextByCourseProgress = (summaryCoreProgressPercentage = 0) => {
  return summaryCoreProgressPercentage === 100 ? 'Review' : 'Resume';
};

const getProgressByLearnerMaterial = (learnerMaterials: $TSFixMe) => {
  return (learnerMaterials && learnerMaterials.summaryCoreProgressPercentage) || 0;
};

const getEnrolledSession = (learnerSessions: $TSFixMe) => {
  return learnerSessions && first(filter(learnerSessions, (item) => item.isEnrolled));
};

export {
  getButtonTextByCourseProgress,
  getProgressByLearnerMaterial,
  getEnrolledSession,
  getTrackingButtonTextByCourseProgress,
};

/**
 * GraphQL to CoursesV1 mapping
 */
// Is course launched
export const isCourseLaunched = (
  courseData: NonNullable<CourseQueryEnterpriseLegacyLearnerHomeQuery['Course']>['queryById']
): boolean => {
  const now = Date.now();
  return courseData?.launchedAt ? new Date(courseData.launchedAt).valueOf() < now : false;
};

// Is course pre-enrolling
export const isCoursePreEnrolling = (
  courseData: NonNullable<CourseQueryEnterpriseLegacyLearnerHomeQuery['Course']>['queryById']
): boolean => {
  const now = Date.now();
  const isLaunched = isCourseLaunched(courseData);
  return courseData?.preEnrollmentEnabledAt
    ? !isLaunched && new Date(courseData.preEnrollmentEnabledAt).valueOf() < now
    : false;
};

// Is course sessions enabled
export const isCourseSessionsEnabled = (
  courseData: NonNullable<CourseQueryEnterpriseLegacyLearnerHomeQuery['Course']>['queryById']
): boolean => {
  const now = Date.now();
  return courseData?.sessionsEnabledAt ? new Date(courseData.sessionsEnabledAt).valueOf() < now : false;
};

// Get course status
export const getCourseStatus = (
  courseData: NonNullable<CourseQueryEnterpriseLegacyLearnerHomeQuery['Course']>['queryById']
): 'preenroll' | 'draft' | 'launched' | 'inactive' => {
  const isLaunched = isCourseLaunched(courseData);
  const isPreEnrolling = isCoursePreEnrolling(courseData);
  return isLaunched && courseData?.isReal ? 'launched' : isPreEnrolling && courseData?.isReal ? 'preenroll' : 'draft';
};

// Get course type
export const getV2CourseType = (
  courseData: NonNullable<CourseQueryEnterpriseLegacyLearnerHomeQuery['Course']>['queryById']
): 'v2.capstone' | 'v2.ondemand' => {
  const isCapstone = courseData?.specializations?.some((s12n: { capstoneCourses?: Array<{ id: string }> }) =>
    s12n.capstoneCourses?.some((capstone: { id: string }) => capstone.id === courseData.id)
  );
  return isCapstone ? 'v2.capstone' : 'v2.ondemand';
};

export const formatGraphqlCourse = (
  courseData: NonNullable<CourseQueryEnterpriseLegacyLearnerHomeQuery['Course']>['queryById']
): Partial<FormattedCourse> | undefined => {
  if (!courseData?.id) {
    return undefined;
  }

  return {
    id: courseData?.id ?? undefined,
    name: courseData?.name ?? undefined,
    slug: courseData?.slug ?? undefined,
    upcomingSessionStartDate: courseData?.upcomingCourseSession?.startsAt
      ? new Date(courseData.upcomingCourseSession.startsAt).getTime()
      : undefined,
    isLaunched: isCourseLaunched(courseData),
    isSessionsEnabled: isCourseSessionsEnabled(courseData),
    plannedLaunchDate: courseData?.plannedLaunchDate ?? undefined,
    courseMode: courseData?.sessionsEnabledAt ? CourseModes.Session : CourseModes.OnDemand,
  };
};
